import { Box, Flex, Grid, Skeleton, SkeletonCircle, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMultisigAccounts, getMultisigAccountData, getWalletTransactions, acknowledgeInvitation } from '../../api/multisigAPI';
import { MultisigAccount } from '../../types/account';
import { Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ApplicationRoutes } from '../../utils/routes';
import { GlobalContext } from '../../providers/Global';
import { useToast } from '../../hooks/useToast';
import HashBasedAvatar from 'components/Avatar';
import { getAvatarUID } from 'utils/avatar';
import InvitationSigningModal from 'components/Modals/InvitationSigningModal';
import InvitationPendingModal from 'components/Modals/InvitationPendingModal';
import WelcomeWalletModal from 'components/Modals/WelcomeWalletModal';
import { TransactionType, ThresholdUpdateParams } from 'types/transactions';
import { ReactComponent as GreenWalletIcon } from 'assets/wallet-icon-green.svg';
import { accountCardGrid, accountCard, invitationCard, invitationBadge, invitationGlow, pendingBadge } from '../styles/MultisigAccounts';

const SEEN_INVITATIONS_KEY = 'multisig_seen_invitations';

const getSeenInvitations = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem(SEEN_INVITATIONS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const markInvitationSeen = (walletId: string) => {
  const seen = getSeenInvitations();
  seen.add(walletId);
  sessionStorage.setItem(SEEN_INVITATIONS_KEY, JSON.stringify(Array.from(seen)));
};

const MultisigAccounts = () => {
  const [accountsData, setAccountsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const seenAtMount = useRef<Set<string>>(getSeenInvitations());

  const {
    connection: { accountId },
    updateAccount,
  } = useContext(GlobalContext);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [invitationModalData, setInvitationModalData] = useState<{
    isOpen: boolean;
    walletName: string;
    walletId: string;
    walletAccountId: string;
    transaction: TransactionType | null;
  }>({
    isOpen: false,
    walletName: '',
    walletId: '',
    walletAccountId: '',
    transaction: null,
  });

  const [pendingModalData, setPendingModalData] = useState<{
    isOpen: boolean;
    walletName: string;
    transaction: TransactionType | null;
  }>({
    isOpen: false,
    walletName: '',
    transaction: null,
  });

  const [welcomeModalData, setWelcomeModalData] = useState<{
    isOpen: boolean;
    walletName: string;
    walletId: string;
    walletAccountId: string;
  }>({
    isOpen: false,
    walletName: '',
    walletId: '',
    walletAccountId: '',
  });

  const handleInvitationClick = async (e: React.MouseEvent, account: MultisigAccount) => {
    e.preventDefault();
    e.stopPropagation();

    const { _id, name, account_id } = account;
    if (!_id) return;

    // Check if this user is already a full owner (ThresholdUpdate already executed).
    // In that case show the welcome modal instead of the signing modal.
    if (!accountId) return;
    const isAlreadyOwner = account.owners?.includes(accountId);

    if (isAlreadyOwner) {
      setWelcomeModalData({
        isOpen: true,
        walletName: name || 'Unnamed Wallet',
        walletId: _id,
        walletAccountId: String(account_id),
      });
      return;
    }

    try {
      // Fetch recent transactions for this wallet
      const result = await getWalletTransactions(_id, 1, 10);
      if (result?.data?.transactions) {
        // Find a pending threshold update that involves this user
        const invitationTx = result.data.transactions.find((tx: TransactionType) =>
          tx.type === 'ThresholdUpdate' &&
          tx.status === 0 && // New (still collecting signatures)
          (tx.params as ThresholdUpdateParams).newSigners?.some((s) => s.accountId === accountId)
        );

        if (invitationTx) {
          const alreadySigned = invitationTx.signatures?.some((s: { accountId: string }) => s.accountId === accountId);

          if (alreadySigned) {
            // User already signed — show pending progress modal
            setPendingModalData({
              isOpen: true,
              walletName: name || 'Unnamed Wallet',
              transaction: invitationTx,
            });
          } else {
            // User hasn't signed yet — show signing modal
            setInvitationModalData({
              isOpen: true,
              walletName: name || 'Unnamed Wallet',
              walletId: _id,
              walletAccountId: String(account_id ?? ''),
              transaction: invitationTx,
            });
          }
        } else {
          // ThresholdUpdate already reconciled — show welcome modal
          setWelcomeModalData({
            isOpen: true,
            walletName: name || 'Unnamed Wallet',
            walletId: _id,
            walletAccountId: String(account_id),
          });
        }
      }
    } catch (error: unknown) {
      console.error('Failed to fetch invitation details:', error);
    }
  };

  const handleWelcomeJoin = async () => {
    const { walletId, walletAccountId } = welcomeModalData;
    try {
      await acknowledgeInvitation(walletId);
      const updatedData = await getMultisigAccountData(walletId);
      if (updatedData?.data) {
        updateAccount(updatedData.data);
      }
      setWelcomeModalData(prev => ({ ...prev, isOpen: false }));
      navigate(`${ApplicationRoutes.Dashboard}/${walletId}/account/${walletAccountId}`);
    } catch (error: unknown) {
      console.error('Failed to acknowledge invitation:', error);
      showToast('Failed to join wallet', 'error');
    }
  };

  const fetchAccountData = useCallback(async () => {
    if (accountId && accountsData.length === 0) {
      setIsLoading(true);
      try {
        const result = await getMultisigAccounts();
        if (result?.data) {
          setAccountsData(result.data);
        }
      } catch (error: unknown) {
        console.error('Failed to fetch multisig accounts:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [accountId, accountsData.length]);

  useEffect(() => {
    window.addEventListener('token', fetchAccountData, false);
  }, [fetchAccountData]);

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  // Determine which invitations should show the one-time glow
  // (newly discovered this session — not yet seen at mount time)
  const glowWalletIds = useMemo(() => {
    if (!accountId || !accountsData.length) return new Set<string>();
    const newGlows = new Set<string>();
    for (const account of accountsData as MultisigAccount[]) {
      const isInvitation = account.pending_owners?.includes(accountId);
      if (isInvitation && account._id && !seenAtMount.current.has(account._id)) {
        newGlows.add(account._id);
      }
    }
    return newGlows;
  }, [accountId, accountsData]);

  // After rendering, mark all current invitations as seen
  useEffect(() => {
    if (!accountId || !accountsData.length) return;
    for (const account of accountsData as MultisigAccount[]) {
      const isInvitation = account.pending_owners?.includes(accountId);
      if (isInvitation && account._id) {
        markInvitationSeen(account._id);
      }
    }
  }, [accountId, accountsData]);

  return (
    <Box
      w="100%"
      p={3}
      bg="white"
      borderRadius={16}
      textAlign="center"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
    >
      <Text mb={6} px={3} size="lg" variant="bodyBold" textAlign="left">
        Multi-Signature Accounts
      </Text>

      <Box maxH="calc(100vh - 200px)" overflowY="auto">
        {isLoading ? (
          <Grid sx={accountCardGrid} px={3} pb={3}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={accountCard} cursor="default" _hover={{}}>
                <Flex alignItems="center" gap={3}>
                  <SkeletonCircle size="36px" />
                  <Box flex={1}>
                    <Skeleton h="14px" w="70%" mb={1.5} borderRadius={4} />
                    <Skeleton h="10px" w="50%" borderRadius={4} />
                  </Box>
                </Flex>
                <Skeleton h="14px" w="40%" borderRadius={4} />
                <Flex gap={2}>
                  <Skeleton h="22px" w="90px" borderRadius={6} />
                  <Skeleton h="22px" w="72px" borderRadius={6} />
                </Flex>
              </Box>
            ))}
          </Grid>
        ) : accountsData.length > 0 ? (
          <Grid
            as={motion.div}
            sx={accountCardGrid}
            px={3}
            pb={3}
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {accountsData.map((account: MultisigAccount) => {
              const { _id, name, account_id, keys, threshold, pending_transactions, balances } = account;
              const isInvitation = accountId ? account.pending_owners?.includes(accountId) : false;
              const shouldGlow = _id ? glowWalletIds.has(_id) : false;

              return (
                <motion.div
                  key={account.account_id}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
                  }}
                >
                <ChakraLink
                  as={RouterLink}
                  to={isInvitation ? '#' : `${ApplicationRoutes.Dashboard}/${_id}/account/${account_id}`}
                  onClick={(e) => isInvitation && handleInvitationClick(e, account)}
                  sx={{
                    ...(isInvitation ? invitationCard : accountCard),
                    ...(shouldGlow ? invitationGlow : {}),
                  }}
                >
                  {/* Header: Avatar + Name + Account ID + Invitation badge */}
                  <Flex alignItems="center" gap={3}>
                    <HashBasedAvatar UID={getAvatarUID(_id)} size={36} />
                    <Box textAlign="left" minW={0} flex={1}>
                      <Text size="sm" variant="bodyBold" isTruncated>
                        {name}
                      </Text>
                      <Text fontSize="xs" color="blackAlpha.500">
                        {account_id}
                      </Text>
                    </Box>
                    {isInvitation && (
                      <Box ml="auto" sx={invitationBadge} flexShrink={0}>
                        <Text fontSize="10px" color="#1a6e32" fontWeight="bold">INVITATION</Text>
                      </Box>
                    )}
                  </Flex>

                  {/* HBAR Balance */}
                  <Text size="sm" fontWeight={600} textAlign="left">
                    {balances?.hbars
                      ? `ℏ ${Number(balances.hbars.replace(/[^0-9.]/g, '')).toFixed(2)}`
                      : 'ℏ —'}
                  </Text>

                  {/* Stats: Signers + Pending */}
                  <Flex gap={2}>
                    <Box sx={pendingBadge} bg="blackAlpha.100" color="blackAlpha.700">
                      {threshold} of {keys?.length} signers
                    </Box>
                    <Box
                      sx={pendingBadge}
                      bg={pending_transactions ? 'orange.400' : 'blackAlpha.100'}
                      color={pending_transactions ? 'white' : 'blackAlpha.500'}
                    >
                      {pending_transactions || 0} pending
                    </Box>
                  </Flex>
                </ChakraLink>
                </motion.div>
              );
            })}
          </Grid>
        ) : (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" minH={240} gap={4}>
            <GreenWalletIcon width={48} height={48} opacity={0.4} />
            <Text size="sm" color="blackAlpha.500">
              Your multisignature wallets will appear here
            </Text>
          </Flex>
        )}
      </Box>

      {invitationModalData.transaction && (
        <InvitationSigningModal
          isOpen={invitationModalData.isOpen}
          onClose={() => setInvitationModalData(prev => ({ ...prev, isOpen: false }))}
          walletName={invitationModalData.walletName}
          walletId={invitationModalData.walletId}
          walletAccountId={invitationModalData.walletAccountId}
          transaction={invitationModalData.transaction}
        />
      )}

      {pendingModalData.transaction && (
        <InvitationPendingModal
          isOpen={pendingModalData.isOpen}
          onClose={() => setPendingModalData(prev => ({ ...prev, isOpen: false }))}
          walletName={pendingModalData.walletName}
          transaction={pendingModalData.transaction}
        />
      )}

      <WelcomeWalletModal
        isOpen={welcomeModalData.isOpen}
        onClose={() => setWelcomeModalData(prev => ({ ...prev, isOpen: false }))}
        walletName={welcomeModalData.walletName}
        walletAccountId={welcomeModalData.walletAccountId}
        onJoin={handleWelcomeJoin}
      />
    </Box>
  );
};

export default MultisigAccounts;
